/* assets/mega-menu-base.js */

// ---------------------------------------------------------------------------
// Rich payload loader
// ---------------------------------------------------------------------------
// header.liquid renders the mega menu in LITE mode: destination links straight
// from the linklist, with no collection lookups, no product loads and no image
// URLs. That keeps every page's HTML small and the links crawlable.
//
// The expensive half — card images and "N tours" counts — lives in
// sections/mega-menu-rich.liquid and is fetched at most once per page, and ONLY
// when both are true:
//
//   * the viewport is >= 990px (below that the mega menu is `display: none`,
//     see the `.header .mega-menus` rules in header.liquid), and
//   * the visitor actually interacts with a nav trigger.
//
// Phones never fire the request, so they pay nothing for a menu they can't see.
// Both mega menus (destinations + travel blogs) share this single fetch.

const MM_DESKTOP_QUERY = '(min-width: 990px)';

// Must be fetched from the LOCALIZED root (e.g. `/tr/`), not `/`, or the panel
// content comes back in the store's default language for every non-English
// visitor. Same pattern as assets/pre-checkout-modal.js.
const MM_RICH_URL =
	(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
		? window.Shopify.routes.root
		: '/') + '?section_id=mega-menu-rich';

let mmRichPromise = null;

function mmIsDesktop() {
	return window.matchMedia(MM_DESKTOP_QUERY).matches;
}

function mmFetchRich() {
	if (mmRichPromise) return mmRichPromise;

	mmRichPromise = fetch(MM_RICH_URL, { credentials: 'same-origin' })
		.then((res) => {
			if (!res.ok) throw new Error(`mega-menu-rich: HTTP ${res.status}`);
			return res.text();
		})
		.then((html) => new DOMParser().parseFromString(html, 'text/html'))
		.catch((err) => {
			// Non-fatal: the lite menu is fully functional on its own, just without
			// thumbnails and counts. Clear the cache so a later hover can retry.
			console.warn('Mega menu: rich payload unavailable, keeping lite menu.', err);
			mmRichPromise = null;
			return null;
		});

	return mmRichPromise;
}

class TripVenturaMegaMenu extends HTMLElement {
	connectedCallback() {
		// Children are guaranteed to be parsed here (the script is deferred), and
		// this is re-entrant-safe if the element is ever moved in the DOM.
		if (this.mmInitialised) return;
		this.mmInitialised = true;

		this.countryTabs = this.querySelectorAll('.mm-tab-item');
		// Find the parent container that triggers the hover state
		this.triggerContainer = this.closest('.nav-item-trigger');

		this.init();
		this.bindHydration();
	}

	init() {
		// 1. Tab Hover Listeners
		this.countryTabs.forEach(tab => {
			tab.addEventListener('mouseenter', () => this.switchCountry(tab));
		});

		// 2. Sidebar Link Hover Listeners (Event Delegation)
		this.addEventListener('mouseenter', (e) => {
			if (e.target.classList && e.target.classList.contains('mm-dest-link')) {
				this.switchDestination(e.target);
			}
		}, true);

		// 3. RESET STATE ON MOUSE LEAVE
		// When mouse leaves the Link OR the Menu, we reset to default
		if (this.triggerContainer) {
			this.triggerContainer.addEventListener('mouseleave', () => {
				// Wait 300ms for the CSS opacity transition to finish
				// so the user doesn't see it "snap" while fading out.
				setTimeout(() => {
					this.resetToDefault();
				}, 300);
			});
		}
	}

	// -------------------------------------------------------------------------
	// Rich hydration
	// -------------------------------------------------------------------------
	// Hydration only ever replaces the contents of each `.mm-grid`. Tabs, sidebar
	// and panel headings are left untouched, so every listener bound in init()
	// stays valid and the merchant's theme-editor heading text is preserved.
	bindHydration() {
		// Defensive: a rich-rendered menu never appears in the live DOM, only in
		// the fetched payload. If one ever did, there is nothing to upgrade.
		if (this.dataset.mmRich === 'true') return;

		const trigger = this.triggerContainer || this;
		const onIntent = () => {
			if (!mmIsDesktop()) return;
			this.hydrate();
		};

		// Not `{ once: true }` — a failed fetch should still be retryable on a
		// later hover, so the listeners are removed only on success.
		trigger.addEventListener('mouseenter', onIntent);
		trigger.addEventListener('focusin', onIntent);
		trigger.addEventListener('touchstart', onIntent, { passive: true });

		this.mmTrigger = trigger;
		this.mmIntentHandler = onIntent;
	}

	releaseHydrationListeners() {
		if (!this.mmTrigger || !this.mmIntentHandler) return;
		this.mmTrigger.removeEventListener('mouseenter', this.mmIntentHandler);
		this.mmTrigger.removeEventListener('focusin', this.mmIntentHandler);
		this.mmTrigger.removeEventListener('touchstart', this.mmIntentHandler);
		this.mmTrigger = null;
		this.mmIntentHandler = null;
	}

	hydrate() {
		if (this.mmHydrated || this.mmHydrating) return;
		this.mmHydrating = true;

		mmFetchRich().then((doc) => {
			this.mmHydrating = false;
			if (!doc) return;

			const menuType = this.dataset.mmType;
			const source = menuType
				? doc.querySelector(`tripventura-mega-menu[data-mm-type="${menuType}"]`)
				: doc.querySelector('tripventura-mega-menu');
			if (!source) return;

			this.querySelectorAll('.mm-content-group').forEach((group) => {
				if (!group.id) return;

				const richGroup = source.querySelector(`#${CSS.escape(group.id)}`);
				if (!richGroup) return;

				const richGrid = richGroup.querySelector('.mm-grid');
				const liveGrid = group.querySelector('.mm-grid');
				if (richGrid && liveGrid) liveGrid.innerHTML = richGrid.innerHTML;
			});

			this.mmHydrated = true;
			this.releaseHydrationListeners();

			// The panel the visitor is looking at right now was just swapped in and
			// still holds unresolved data-src images.
			const activePanel = this.querySelector('.mm-content-group.active');
			if (activePanel) this.loadImages(activePanel);
		});
	}

	resetToDefault() {
		// Simply simulate hovering the first tab.
		// This triggers the cascade: Country 1 -> Sidebar 1 -> Dest 1 -> Grid 1
		const firstTab = this.countryTabs[0];
		if (firstTab) {
			this.switchCountry(firstTab);
		}
	}

	switchCountry(tab) {
		// remove active from all tabs
		this.countryTabs.forEach(t => t.classList.remove('active'));
		// add active to this tab
		tab.classList.add('active');

		const countryId = tab.dataset.target;

		// Hide all Sidebar Groups
		this.querySelectorAll('.mm-sidebar-group').forEach(group => {
			group.classList.remove('active');
		});

		// Show current Country Sidebar Group
		const activeSidebar = this.querySelector(`#sidebar-${countryId}`);
		if (activeSidebar) {
			activeSidebar.classList.add('active');

			// AUTO-SELECT FIRST DESTINATION
			// When switching countries, always reset to the first destination in the list
			const firstDestLink = activeSidebar.querySelector('.mm-dest-link');
			if (firstDestLink) {
				this.switchDestination(firstDestLink);
			}
		}
	}

	switchDestination(link) {
		// Reset active links in THIS sidebar group only
		const parentGroup = link.closest('.mm-sidebar-group');
		if (parentGroup) {
			parentGroup.querySelectorAll('.mm-dest-link').forEach(l => l.classList.remove('active'));
		}

		link.classList.add('active');
		const contentId = link.dataset.contentId;

		// Hide all content panels
		this.querySelectorAll('.mm-content-group').forEach(panel => {
			panel.classList.remove('active');
		});

		// Show target content panel
		const activePanel = this.querySelector(`#${contentId}`);
		if (activePanel) {
			activePanel.classList.add('active');

			// TRIGGER IMAGE LOAD
			// This function is smart: it only loads images that have 'data-src'.
			// Once loaded, 'data-src' is removed, so it never runs twice.
			this.loadImages(activePanel);
		}
	}

	loadImages(container) {
		// Select ONLY images that still need loading
		const lazyImages = container.querySelectorAll('img[data-src]');

		if (lazyImages.length > 0) {
			lazyImages.forEach(img => {
				img.src = img.dataset.src;
				img.removeAttribute('data-src'); // Prevents future re-loading
			});
		}
	}
}

customElements.define('tripventura-mega-menu', TripVenturaMegaMenu);

// ---------------------------------------------------------------------------
// Desktop idle warm-up
// ---------------------------------------------------------------------------
// Hover alone would mean the panel is briefly empty while the request is in
// flight. Warming the cache after `load` + idle means the payload is normally
// already in memory by the time anyone hovers, so hydrate() is a pure DOM swap.
//
// This only warms the network cache — the DOM is still untouched until real
// intent, and the desktop gate means phones never issue the request.
function mmWarmOnIdle() {
	if (!mmIsDesktop()) return;

	const warm = () => mmFetchRich();
	if ('requestIdleCallback' in window) {
		requestIdleCallback(warm, { timeout: 3000 });
	} else {
		setTimeout(warm, 1500);
	}
}

if (document.readyState === 'complete') {
	mmWarmOnIdle();
} else {
	window.addEventListener('load', mmWarmOnIdle, { once: true });
}
